import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from 'react';
import {
  getNumericPrice,
  getNumericDuration,
  getTotalDuration as calculateTotalDuration,
  getTotalPrice as calculateTotalPrice,
  normalizeService
} from '../../utils/bookingUtils';
import { supabase } from '../../lib/supabaseClient';
import { clearTimeSlotCache } from '../../lib/supabaseService';

// LocalStorage key for persisting booking state
const BOOKING_STORAGE_KEY = 'lotus_spa_booking_state';
const BOOKING_RESTORE_KEY = 'lotus_booking_restore';

// Define types for our booking data
export type Service = {
  id: string;
  name: string;
  duration: number | string; // in minutes
  price: number | string;
  category_id: string;
  short_description?: string;
  description?: string;
  numericPrice?: number;
  numericDuration?: number;
  discountedPrice?: string; // Promotional/sale price (if set, use this instead of price)
};

export type StaffMember = {
  id: string;
  user_id: string | null;
  name: string;
  role: string;
  photo: string;
};

export type CustomerInfo = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes?: string;
};

export type CreditToUse = {
  serviceId: string;
  quantity: number;
};

export type BookingState = {
  selectedServices: Service[];
  selectedStaff: StaffMember | null;
  selectedDate: Date | null;
  selectedTime: string | null;
  customerInfo: CustomerInfo | null;
  currentStep: number;
  paymentIntentId: string | null;
  paymentStatus: 'pending' | 'processing' | 'succeeded' | 'failed' | null;
  appointmentId: string | null;
  creditsToUse: CreditToUse[];  // Member credits to apply
  editingAppointmentId: string | null;  // null = create mode, string = edit mode
  editClientId: string | null;  // client_id from cancelled appointment for rebooking
};

// Helper functions for localStorage
const saveBookingToStorage = (state: BookingState) => {
  try {
    const stateToSave = {
      ...state,
      selectedDate: state.selectedDate?.toISOString() || null,
    };
    localStorage.setItem(BOOKING_STORAGE_KEY, JSON.stringify(stateToSave));
  } catch (error) {
    console.error('Failed to save booking to localStorage:', error);
  }
};

const loadBookingFromStorage = (): BookingState | null => {
  try {
    const saved = localStorage.getItem(BOOKING_STORAGE_KEY);
    if (!saved) return null;
    
    const parsed = JSON.parse(saved);
    return {
      ...parsed,
      selectedDate: parsed.selectedDate ? new Date(parsed.selectedDate) : null,
    };
  } catch (error) {
    console.error('Failed to load booking from localStorage:', error);
    return null;
  }
};

const clearBookingFromStorage = () => {
  try {
    localStorage.removeItem(BOOKING_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear booking from localStorage:', error);
  }
};

// Define the context type
type BookingContextType = {
  bookingState: BookingState;
  addService: (service: Service) => void;
  removeService: (serviceId: string) => void;
  setSelectedStaff: (staff: StaffMember | null) => void;
  setSelectedDate: (date: Date | null) => void;
  setSelectedTime: (time: string | null) => void;
  setCustomerInfo: (info: CustomerInfo) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  resetBooking: () => void;
  getTotalDuration: () => number;
  getTotalPrice: () => number;
  setPaymentIntent: (paymentIntentId: string) => void;
  setPaymentStatus: (status: 'pending' | 'processing' | 'succeeded' | 'failed') => void;
  setAppointmentId: (appointmentId: string) => void;
  saveBookingState: () => void;
  clearBookingState: () => void;
  // Credit functions
  setCreditsToUse: (credits: CreditToUse[]) => void;
  toggleCreditForService: (serviceId: string, maxQuantity: number) => void;
  clearCredits: () => void;
  // Edit mode
  initEditMode: (appointmentId: string) => Promise<void>;
  isEditMode: boolean;
};

// Initial state
const initialState: BookingState = {
  selectedServices: [],
  selectedStaff: null,
  selectedDate: null,
  selectedTime: null,
  customerInfo: null,
  currentStep: 1,
  paymentIntentId: null,
  paymentStatus: null,
  appointmentId: null,
  creditsToUse: [],
  editingAppointmentId: null,
  editClientId: null,
};

// Create context with default values
const BookingContext = createContext<BookingContextType>({
  bookingState: initialState,
  addService: () => {},
  removeService: () => {},
  setSelectedStaff: () => {},
  setSelectedDate: () => {},
  setSelectedTime: () => {},
  setCustomerInfo: () => {},
  nextStep: () => {},
  prevStep: () => {},
  goToStep: () => {},
  resetBooking: () => {},
  getTotalDuration: () => 0,
  getTotalPrice: () => 0,
  setPaymentIntent: () => {},
  setPaymentStatus: () => {},
  setAppointmentId: () => {},
  saveBookingState: () => {},
  clearBookingState: () => {},
  setCreditsToUse: () => {},
  toggleCreditForService: () => {},
  clearCredits: () => {},
  initEditMode: async () => {},
  isEditMode: false,
});

/**
 * Provider component for the booking context
 */
export const BookingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [bookingState, setBookingState] = useState<BookingState>(() => {
    // Try to load saved booking state on initial mount
    const saved = loadBookingFromStorage();
    
    // If saved state is a completed booking, clear it and start fresh
    if (saved && saved.appointmentId && saved.paymentStatus === 'succeeded') {
      clearBookingFromStorage();
      return initialState;
    }

    // Otherwise restore saved state (for incomplete bookings or Afterpay returns)
    if (saved) {
      return saved;
    }
    
    return initialState;
  });

  // Load booking from localStorage on mount (for Afterpay returns)
  useEffect(() => {
    const saved = loadBookingFromStorage();

    // Only restore if it's an incomplete booking with appointmentId (Afterpay redirect)
    if (saved && saved.appointmentId && saved.paymentStatus !== 'succeeded') {
      setBookingState(saved);
    }
  }, []);

  // Handle restore after login (when user was prompted to login during guest booking)
  useEffect(() => {
    const handleRestoreAfterLogin = async () => {
      const shouldRestore = localStorage.getItem(BOOKING_RESTORE_KEY);

      if (!shouldRestore) return;

      // Check if user is logged in
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) return;

      // Clear the restore flag
      localStorage.removeItem(BOOKING_RESTORE_KEY);

      // Get the saved booking state
      const saved = loadBookingFromStorage();
      if (!saved || saved.selectedServices.length === 0) return;

      // Fetch user profile to update customer info
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profile) {
        // Update customer info with logged-in user's profile
        const nameParts = profile.full_name ? profile.full_name.split(' ') : ['', ''];
        const firstName = profile.preferred_name || nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        const updatedCustomerInfo = {
          firstName,
          lastName,
          email: profile.email || user.email || '',
          phone: profile.phone_number || '',
          notes: saved.customerInfo?.notes || ''
        };

        // Restore booking and go to step 5 (review)
        setBookingState({
          ...saved,
          customerInfo: updatedCustomerInfo,
          currentStep: 5
        });
      }
    };

    handleRestoreAfterLogin();
  }, []);

  const addService = useCallback((service: Service) => {
    const normalizedService = normalizeService(service);
    setBookingState(prev => ({
      ...prev,
      selectedServices: [...prev.selectedServices, normalizedService]
    }));
  }, []);

  const removeService = useCallback((serviceId: string) => {
    setBookingState(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.filter(s => s.id !== serviceId)
    }));
  }, []);

  const setSelectedStaff = useCallback((staff: StaffMember | null) => {
    setBookingState(prev => ({ ...prev, selectedStaff: staff }));
  }, []);

  const setSelectedDate = useCallback((date: Date | null) => {
    setBookingState(prev => ({ ...prev, selectedDate: date }));
  }, []);

  const setSelectedTime = useCallback((time: string | null) => {
    setBookingState(prev => ({ ...prev, selectedTime: time }));
  }, []);

  const setCustomerInfo = useCallback((info: CustomerInfo) => {
    setBookingState(prev => ({ ...prev, customerInfo: info }));
  }, []);

  const nextStep = useCallback(() => {
    setBookingState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
  }, []);

  const prevStep = useCallback(() => {
    setBookingState(prev => {
      const newStep = Math.max(1, prev.currentStep - 1);

      // Clear appointment state when leaving payment step (step 6)
      if (prev.currentStep === 6) {
        return {
          ...prev,
          currentStep: newStep,
          appointmentId: null,
          paymentIntentId: null,
          paymentStatus: null,
        };
      }

      return { ...prev, currentStep: newStep };
    });
  }, []);

  const goToStep = useCallback((step: number) => {
    setBookingState(prev => ({ ...prev, currentStep: step }));
  }, []);

  const resetBooking = useCallback(() => {
    setBookingState(initialState);
    clearBookingFromStorage();
  }, []);

  const saveBookingState = useCallback(() => {
    saveBookingToStorage(bookingState);
  }, [bookingState]);

  const clearBookingState = useCallback(() => {
    clearBookingFromStorage();
  }, []);

  const getTotalDuration = useCallback(() => {
    return calculateTotalDuration(bookingState.selectedServices);
  }, [bookingState.selectedServices]);

  const getTotalPrice = useCallback(() => {
    return calculateTotalPrice(bookingState.selectedServices);
  }, [bookingState.selectedServices]);

  const setPaymentIntent = useCallback((paymentIntentId: string) => {
  setBookingState(prev => ({ ...prev, paymentIntentId }));
}, []);

const setPaymentStatus = useCallback((status: 'pending' | 'processing' | 'succeeded' | 'failed') => {
  setBookingState(prev => ({ ...prev, paymentStatus: status }));
}, []);

const setAppointmentId = useCallback((appointmentId: string) => {
  setBookingState(prev => ({ ...prev, appointmentId }));
}, []);

// Credit functions
const setCreditsToUse = useCallback((credits: CreditToUse[]) => {
  setBookingState(prev => ({ ...prev, creditsToUse: credits }));
}, []);

const toggleCreditForService = useCallback((serviceId: string, maxQuantity: number) => {
  setBookingState(prev => {
    // Count how many of this service are in the cart
    const serviceCountInCart = prev.selectedServices.filter(s => s.id === serviceId).length;

    // Find existing credit for this service
    const existingCredit = prev.creditsToUse.find(c => c.serviceId === serviceId);

    if (existingCredit) {
      // Remove credit if it exists
      return {
        ...prev,
        creditsToUse: prev.creditsToUse.filter(c => c.serviceId !== serviceId)
      };
    } else {
      // Add credit - use as many as possible (up to cart quantity or available)
      const quantityToUse = Math.min(serviceCountInCart, maxQuantity);
      if (quantityToUse > 0) {
        return {
          ...prev,
          creditsToUse: [...prev.creditsToUse, { serviceId, quantity: quantityToUse }]
        };
      }
    }
    return prev;
  });
}, []);

const clearCredits = useCallback(() => {
  setBookingState(prev => ({ ...prev, creditsToUse: [] }));
}, []);

const isEditMode = bookingState.editingAppointmentId !== null;

const initEditMode = useCallback(async (appointmentId: string) => {
  try {
    // Fetch appointment with services and staff
    const { data: appointment, error: aptError } = await supabase
      .from('appointments')
      .select(`
        id,
        staff_id,
        client_id,
        start_time,
        end_time,
        status,
        payment_status,
        notes,
        team!appointments_staff_id_fkey (
          id,
          user_id,
          name,
          role,
          photo
        ),
        appointment_services (
          service_id,
          price,
          duration,
          services (
            id,
            name,
            duration,
            price,
            category_id,
            short_description,
            description
          )
        )
      `)
      .eq('id', appointmentId)
      .single();

    if (aptError || !appointment) {
      console.error('[initEditMode] Failed to fetch appointment:', aptError);
      return;
    }

    // Cancel the existing appointment to free the time slot
    const { error: cancelError } = await supabase.rpc('cancel_appointment', {
      p_appointment_id: appointmentId,
    });
    if (cancelError) {
      console.error('[initEditMode] Failed to cancel appointment for editing:', cancelError);
      return; // Don't enter edit mode if cancel fails
    }

    // Clear cached time slots so the freed slot appears available
    clearTimeSlotCache();

    // Fetch client profile for customerInfo
    const { data: clientProfile } = await supabase
      .from('user_profiles')
      .select('full_name, preferred_name, email, phone_number')
      .eq('id', (appointment as any).client_id)
      .single();

    // Build selected services from appointment_services
    const services: Service[] = ((appointment as any).appointment_services || []).map((as: any) => {
      const svc = as.services;
      return normalizeService({
        id: svc.id,
        name: svc.name,
        duration: svc.duration,
        price: as.price || svc.price,
        category_id: svc.category_id,
        short_description: svc.short_description,
        description: svc.description,
      });
    });

    // Build staff member
    const team = (appointment as any).team;
    const staff: StaffMember | null = team ? {
      id: team.id,
      user_id: team.user_id,
      name: team.name,
      role: team.role,
      photo: team.photo,
    } : null;

    // Build customer info
    const nameParts = clientProfile?.full_name ? clientProfile.full_name.split(' ') : ['', ''];
    const firstName = clientProfile?.preferred_name || nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const customerInfo: CustomerInfo = {
      firstName,
      lastName,
      email: clientProfile?.email || '',
      phone: clientProfile?.phone_number || '',
      notes: (appointment as any).notes || '',
    };

    // Parse start_time for selectedDate and selectedTime
    const startTimeStr = (appointment as any).start_time;
    const utcDateString = startTimeStr.endsWith('Z') ? startTimeStr : startTimeStr + 'Z';
    const selectedTime = utcDateString;
    const selectedDate = new Date(utcDateString);

    const newState: BookingState = {
      selectedServices: services,
      selectedStaff: staff,
      selectedDate,
      selectedTime,
      customerInfo,
      currentStep: 1,
      paymentIntentId: null,
      paymentStatus: null,
      appointmentId: appointmentId,
      creditsToUse: [],
      editingAppointmentId: appointmentId,
      editClientId: (appointment as any).client_id,
    };

    setBookingState(newState);
    saveBookingToStorage(newState);
  } catch (err) {
    console.error('[initEditMode] Error:', err);
  }
}, []);

const contextValue = {
  bookingState,
  addService,
  removeService,
  setSelectedStaff,
  setSelectedDate,
  setSelectedTime,
  setCustomerInfo,
  nextStep,
  prevStep,
  goToStep,
  resetBooking,
  getTotalDuration,
  getTotalPrice,
  setPaymentIntent,
  setPaymentStatus,
  setAppointmentId,
  saveBookingState,
  clearBookingState,
  setCreditsToUse,
  toggleCreditForService,
  clearCredits,
  initEditMode,
  isEditMode,
};

  return (
    <BookingContext.Provider value={contextValue}>
      {children}
    </BookingContext.Provider>
  );
};

// Custom hook to use the booking context
export const useBooking = () => useContext(BookingContext);

export default BookingContext;
